// based on https://www.radix-ui.com/primitives/docs/components/select#select

'use client';

import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cva, cx, VariantProps } from 'class-variance-authority';
import styles from './select.module.scss';
import { Checkmark, ChevronDown, ChevronUp } from '../../../icons';

const SelectRoot = SelectPrimitive.Root;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTriggerClass = cva(styles.selectTrigger, {
  variants: {
    variant: {
      default: styles.selectTrigger,
      chip: styles.selectTriggerChip
    }
  },
  defaultVariants: {}
});

type SelectTriggerProps = VariantProps<typeof SelectTriggerClass> & {
  className?: string;
  children: React.ReactNode;
};

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
    SelectTriggerProps
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={SelectTriggerClass({
      className,
      variant: props.variant
    })}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <ChevronDown />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cx(styles.selectScrollUpButton, className)}
    {...props}
  >
    <ChevronUp />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cx(styles.selectScrollDownButton, className)}
    {...props}
  >
    <ChevronDown />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & {
    usePortal?: boolean;
  }
>(
  (
    { className, children, position = 'popper', usePortal = true, ...props },
    ref
  ) => {
    const renderContent = () => {
      return (
        <SelectPrimitive.Content
          ref={ref}
          className={cx(
            styles.selectContent,
            position === 'popper' && styles.selectContentPopper,
            className
          )}
          position={position}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cx(
              styles.selectViewport,
              position === 'popper' && styles.selectViewportPopper
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      );
    };

    return usePortal ? (
      <SelectPrimitive.Portal>{renderContent()}</SelectPrimitive.Portal>
    ) : (
      renderContent()
    );
  }
);
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cx(styles.selectLabel, className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cx(styles.selectItem, className)}
    {...props}
  >
    <span className={styles.selectItemIndicator}>
      <SelectPrimitive.ItemIndicator>
        <Checkmark />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cx(styles.selectSeparator, className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  SelectRoot,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton
};

export type OptionBase = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type SelectProps<T extends OptionBase> = {
  placeholder?: string;
  options: T[];
  id?: string;
  triggerProps?: Partial<React.ComponentPropsWithoutRef<typeof SelectTrigger>>;
} & React.ComponentPropsWithoutRef<typeof SelectRoot>;

export default function Select<T extends OptionBase>({
  placeholder,
  options,
  triggerProps,
  ...props
}: SelectProps<T>) {
  return (
    <SelectRoot {...props}>
      <SelectTrigger id={props.id} {...triggerProps}>
        <SelectValue placeholder={placeholder || 'Select'} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            disabled={option.disabled}
            value={option.value}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </SelectRoot>
  );
}
